import React, { useEffect, useMemo, useRef } from 'react';
import { visuallyHidden } from '@noths/polaris-client-styles';
import type { ReviewPreviewCardPayload } from '@noths/polaris-design-system-candidate-components';
import { ReviewPreviewCard } from '@noths/polaris-design-system-candidate-components';
import { Icon } from '@noths/polaris-design-system-core-components';
import type { Review } from '@noths/polaris-design-system-ooux-interfaces';

import { RatingHistogram } from 'src/components/molecules/RatingHistogram/components/RatingHistogram';
import type { ReviewsSummaryRatingBreakdown } from 'src/redux/reviews/types';
import { roundAverageProductRating } from 'src/utils/roundAverageProductRating';
import * as copy from '../constants/copy';
import { FeefoAttribution } from './FeefoAttribution';
import type { ReviewPhotoLink } from './ReviewPhotosRail';
import { ReviewPhotosRail } from './ReviewPhotosRail';
import { ReviewsPagination } from './ReviewsPagination';
import * as styles from './CustomersReviewsDrawer.styles';

const SKELETON_KEYS = ['first', 'second', 'third'];

export interface CustomersReviewsDrawerContentProps {
  /** When the cards are read; makes their dates relative. */
  date?: Date;
  error?: boolean;
  /** Where each card's controls lead: this drawer, deep-linked to that review. */
  hrefForReview: (reviewId: string) => string;
  onClose: () => void;
  onControlClick?: (
    e: React.MouseEvent<HTMLAnchorElement>,
    payload: ReviewPreviewCardPayload,
  ) => void;
  onPageChange: (page: number, trigger: 'next' | 'page' | 'previous') => void;
  /** The "how does this rating work" link beside the Feefo logo. */
  onRatingSystemInfoClick: () => void;
  page: number;
  /** Feefo's average and per-star counts. Null until the breakdown settles. */
  ratingBreakdown: ReviewsSummaryRatingBreakdown | null;
  /** `null` while the page is in flight, which renders the skeleton. */
  reviews: Review[] | null;
  /** The product's total, from the page's single source, so it matches the "See all" button. */
  totalCount: number;
  totalPages: number;
}

/**
 * Everything inside the reviews drawer's list panel. Separate from `CustomersReviewsDrawer`
 * because the react-modal portal lays out to zero height in Storybook, so only the content
 * can be Percy-snapshotted. The cards are previews: "Read more" opens the review in a panel
 * over the list. Stacked in a column they have no height to fill, so each is as tall as its
 * own content.
 */
export const CustomersReviewsDrawerContent = ({
  date,
  error = false,
  hrefForReview,
  onClose,
  onControlClick,
  onPageChange,
  onRatingSystemInfoClick,
  page,
  ratingBreakdown,
  reviews,
  totalCount,
  totalPages,
}: CustomersReviewsDrawerContentProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  // Scroll to the top on a page change; the pagination is at the bottom, so otherwise the
  // new page loads above, out of sight. Keyed on `page` so it fires on the request, not
  // the arrival.
  useEffect(() => {
    const content = contentRef.current;

    if (content) {
      // scrollTop, not scrollTo: jsdom has no scrollTo and threw on mount.
      content.scrollTop = 0;
    }
  }, [page]);

  // Derived from the reviews on show rather than passed in, so the rail pages with the list.
  const photos = useMemo<ReviewPhotoLink[]>(
    () =>
      (reviews ?? []).flatMap((review) =>
        (review.photos ?? []).map((photo, index) => ({
          alt: photo.alt,
          href: hrefForReview(review.id),
          key: `${review.id}-${index}`,
          url: photo.url,
        })),
      ),
    [hrefForReview, reviews],
  );

  return (
    <section css={styles.section}>
      <header css={styles.header}>
        <button css={styles.closeButton} onClick={onClose} type="button">
          <Icon aria-hidden name="close" size="md" />
          {copy.CLOSE}
        </button>
      </header>

      <div css={styles.content} ref={contentRef}>
        <div css={styles.summary}>
          {/* Omitted, not zeroed, until the breakdown settles. */}
          {ratingBreakdown && (
            <>
              {/* A product without ratings has an average of 0; the histogram says so instead. */}
              {roundAverageProductRating(ratingBreakdown.averageRating) > 0 && (
                <p css={styles.average}>
                  <Icon aria-hidden name="starFull" size="md" />
                  <span aria-hidden>
                    {copy.averageRatingDisplay(ratingBreakdown.averageRating)}
                  </span>
                  <span css={visuallyHidden}>
                    {copy.averageRatingLabel(ratingBreakdown.averageRating)}
                  </span>
                </p>
              )}
              <RatingHistogram starCounts={ratingBreakdown.starCounts} />
            </>
          )}
          <FeefoAttribution onRatingSystemInfoClick={onRatingSystemInfoClick} />
        </div>

        <hr css={styles.divider} />

        <div css={styles.headingRow}>
          <h2 css={styles.heading}>{copy.reviewCountHeading(totalCount)}</h2>
        </div>

        {photos.length > 0 && <ReviewPhotosRail photos={photos} />}

        {error && <p css={styles.message}>{copy.ERROR_REVIEWS}</p>}

        {!error && reviews === null && (
          <>
            <p css={styles.message}>{copy.LOADING_REVIEWS}</p>
            <ul aria-hidden css={styles.list} data-testid="reviews-drawer-skeleton">
              {SKELETON_KEYS.map((key) => (
                <li css={styles.skeletonItem} key={key} />
              ))}
            </ul>
          </>
        )}

        {!error && reviews?.length === 0 && <p css={styles.message}>{copy.NO_REVIEWS}</p>}

        {!error && reviews && reviews.length > 0 && (
          <ul css={styles.list} data-testid="reviews-drawer-list">
            {reviews.map((review) => (
              <li key={review.id}>
                <ReviewPreviewCard
                  date={date}
                  href={hrefForReview(review.id)}
                  onControlClick={onControlClick}
                  review={review}
                />
              </li>
            ))}
          </ul>
        )}

        <ReviewsPagination currentPage={page} onPageChange={onPageChange} totalPages={totalPages} />
      </div>
    </section>
  );
};

CustomersReviewsDrawerContent.displayName = 'CustomersReviewsDrawerContent';
